// Top resource bar — see docs/07-art/macro-ui-wireframes.md §2.
// Shows a per-tick delta next to each numeric resource so the player can
// see at a glance whether the economy is gaining or bleeding.

import { Clock, Coins, Hammer, Scroll, Sun, Wheat } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from './lib/utils.js';

export interface ResourceBarProps {
  readonly food: number;
  readonly industry: number;
  readonly knowledge: number;
  readonly faith: number;
  readonly treasury: number;
  readonly tickLabel: string;
  readonly className?: string;
  readonly children?: React.ReactNode;
}

export type ResourceDeltaDirection = 'up' | 'down' | 'none';

export interface ResourceDelta {
  readonly direction: ResourceDeltaDirection;
  readonly delta: number;
}

/**
 * Pure delta computation — extracted so the math stays testable without
 * rendering. The component feeds `previous` from a ref and `current` from
 * the latest prop.
 */
export function computeResourceDelta(previous: number, current: number): ResourceDelta {
  const delta = current - previous;
  if (delta === 0) return { direction: 'none', delta: 0 };
  return { direction: delta > 0 ? 'up' : 'down', delta };
}

interface ResourceSpec {
  readonly key: 'food' | 'industry' | 'knowledge' | 'faith' | 'treasury';
  readonly icon: React.ReactNode;
}

const RESOURCE_SPECS: ReadonlyArray<ResourceSpec> = [
  { key: 'food', icon: <Wheat className="size-3.5" /> },
  { key: 'industry', icon: <Hammer className="size-3.5" /> },
  { key: 'knowledge', icon: <Scroll className="size-3.5" /> },
  { key: 'faith', icon: <Sun className="size-3.5" /> },
  { key: 'treasury', icon: <Coins className="size-3.5" /> },
];

export function ResourceBar(props: ResourceBarProps) {
  const prev = useRef<Record<ResourceSpec['key'], number>>({
    food: props.food,
    industry: props.industry,
    knowledge: props.knowledge,
    faith: props.faith,
    treasury: props.treasury,
  });

  // Snapshot the values from this render so the next render can diff against
  // them. We use an effect (not an in-render assignment) to keep the
  // ref update out of the render phase.
  useEffect(() => {
    prev.current = {
      food: props.food,
      industry: props.industry,
      knowledge: props.knowledge,
      faith: props.faith,
      treasury: props.treasury,
    };
  });

  return (
    <output
      aria-live="polite"
      className={cn(
        'flex w-full flex-wrap items-center gap-1 border-b border-border bg-card/85 px-3 py-1.5 text-[13px] tabular-nums tracking-wide text-foreground backdrop-blur-sm',
        props.className,
      )}
    >
      <Cell icon={<Clock className="size-3.5" />} label={props.tickLabel} />
      {RESOURCE_SPECS.map(({ key, icon }) => {
        const current = props[key];
        const { direction, delta } = computeResourceDelta(prev.current[key], current);
        return <Cell key={key} icon={icon} value={current} delta={direction} deltaAmount={delta} />;
      })}
      {props.children}
    </output>
  );
}

interface CellProps {
  readonly icon: React.ReactNode;
  readonly value?: number;
  readonly label?: string;
  readonly delta?: ResourceDeltaDirection;
  readonly deltaAmount?: number;
}

function Cell({ icon, value, label, delta = 'none', deltaAmount = 0 }: CellProps) {
  const deltaColor =
    delta === 'up'
      ? 'text-emerald-700 dark:text-emerald-400'
      : delta === 'down'
        ? 'text-rose-700 dark:text-rose-400'
        : 'text-transparent';
  const sign = deltaAmount > 0 ? '+' : '';
  return (
    <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1">
      <span className="self-center text-muted-foreground">{icon}</span>
      <span className="whitespace-nowrap">{label ?? value}</span>
      {delta !== 'none' && (
        <span className={cn('text-[10px] tabular-nums', deltaColor)} aria-hidden>
          {sign}
          {deltaAmount}
        </span>
      )}
    </span>
  );
}
